import { useState, type FormEvent } from "react";
import Reveal from "@/components/feature/Reveal";

const FORM_URL = "https://readdy.ai/api/form/dah6na3oh653ivfvnmhg";

type Status = "idle" | "loading" | "success" | "error";

const perks = [
  "有新节点被确认时，收到整理好的摘要",
  "只在有实质补充时推送，不做无意义打扰",
  "随时可以退订，邮箱不会被用于其他用途",
];

export default function NewsletterSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("company_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，我们会把整理好的更新摘要发送到你的邮箱。");
      form.reset();
      return;
    }

    setStatus("loading");
    setMessage("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，我们会把整理好的更新摘要发送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(typeof serverMsg === "string" && serverMsg.trim() !== "" ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后再试。");
    }
  };

  return (
    <section id="subscribe" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-8">
        <div className="overflow-hidden rounded-lg bg-primary-500 px-6 py-10 md:px-14 md:py-14">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1.5 font-label text-xs text-background-50">
                <i className="ri-notification-3-line text-accent-400"></i>
                更新提醒
              </span>
              <h2 className="mt-5 font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
                有新节点补充时
                <br className="hidden md:block" />
                第一时间告诉你
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-50/85 md:text-base">
                留下邮箱，当灵灵黑料事件汇总出现新的关键节点时，你会收到一份整理好的摘要，不用自己反复来翻。
              </p>

              <ul className="mt-6 flex flex-col gap-3">
                {perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-3 text-sm text-background-50/90">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background-50/20">
                      <i className="ri-check-line text-xs text-accent-400"></i>
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            <Reveal>
              <form
                data-readdy-form
                id="lingling-subscribe-form"
                onSubmit={handleSubmit}
                className="rounded-lg bg-background-50 p-5 md:p-7"
              >
                <h3 className="font-heading text-lg font-bold text-foreground-950">订阅更新摘要</h3>
                <p className="mt-1.5 font-label text-xs text-foreground-500">仅需邮箱，随时可退订。</p>

                <div className="mt-5 flex flex-col gap-4">
                  <div>
                    <label htmlFor="subscribe-name" className="font-label text-xs text-foreground-600">
                      你的称呼
                    </label>
                    <input
                      id="subscribe-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="mt-1.5 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="subscribe-email" className="font-label text-xs text-foreground-600">
                      邮箱地址
                    </label>
                    <input
                      id="subscribe-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="mt-1.5 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="subscribe-interest" className="font-label text-xs text-foreground-600">
                      最想先收到
                    </label>
                    <select
                      id="subscribe-interest"
                      name="interest"
                      defaultValue="时间线节点更新"
                      className="mt-1.5 h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    >
                      <option value="时间线节点更新">时间线节点更新</option>
                      <option value="全盘点合集补充">全盘点合集补充</option>
                      <option value="图文长图上新">图文长图上新</option>
                      <option value="阶段复盘小结">阶段复盘小结</option>
                    </select>
                  </div>

                  <input
                    className="field-company-alt"
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="mt-1 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-wait disabled:opacity-70"
                  >
                    <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    {status === "loading" ? "提交中…" : "订阅更新"}
                  </button>

                  {message && (
                    <p
                      className={`rounded-md px-3 py-2.5 font-label text-xs leading-relaxed ${
                        status === "success" ? "bg-accent-100 text-accent-900" : "bg-primary-100 text-primary-800"
                      }`}
                    >
                      {message}
                    </p>
                  )}
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}